'use client';

import React, { useState } from 'react';
import styled from 'styled-components';
import { Accordion } from '../Accordion/Accordion';

export interface QuickViewLogsProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[];
  title?: string;
  subtitle?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRecordLabel?: (record: any, index: number) => string;
  className?: string;
}

const Container = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 32px;
  margin-top: 24px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const RecordsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AccordionTitle = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
`;

const LogLevelBadge = styled.span<{ $level: string }>`
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  flex-shrink: 0;
  margin-top: 2px;
  
  background: ${({ $level }) => {
    const level = $level.toLowerCase();
    if (level === 'error') return '#fee2e2';
    if (level === 'warn' || level === 'warning') return '#fef3c7';
    if (level === 'info') return '#dbeafe';
    if (level === 'debug') return '#f3e8ff';
    return '#f3f4f6';
  }};
  
  color: ${({ $level }) => {
    const level = $level.toLowerCase();
    if (level === 'error') return '#dc2626';
    if (level === 'warn' || level === 'warning') return '#d97706';
    if (level === 'info') return '#2563eb';
    if (level === 'debug') return '#7c3aed';
    return '#6b7280';
  }};
`;

const RecordContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const RecordTimestamp = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 6px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
`;

const RecordMessage = styled.div`
  font-size: 14px;
  color: #111827;
  font-weight: 400;
  line-height: 1.5;
  margin-bottom: 4px;
`;

const RecordSource = styled.div`
  font-size: 12px;
  color: #9ca3af;
`;

const JSONViewerContainer = styled.div`
  padding: 0;
  background: #1e1e1e;
  border-top: 1px solid #e5e7eb;
  margin-top: 12px;
`;

const JSONContainer = styled.div`
  padding: 20px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
  font-size: 13px;
  line-height: 1.6;
  color: #e5e5e5;
  overflow-x: auto;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #2a2a2a;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #4a4a4a;
    border-radius: 3px;
  }
`;

const JSONKey = styled.span`
  color: #e5e5e5;
`;

const JSONString = styled.span`
  color: #FB6F2A;
`;

const JSONNumber = styled.span`
  color: #FB6F2A;
`;

const JSONBoolean = styled.span`
  color: #FB6F2A;
`;

const JSONNull = styled.span`
  color: #64748b;
`;

const JSONBracket = styled.span`
  color: #9ca3af;
`;

const CollapsibleObject = styled.div`
  margin-left: 16px;
`;

const ToggleButton = styled.button`
  background: transparent;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  margin-right: 4px;
  font-size: 12px;
  
  &:hover {
    color: #e5e5e5;
  }
`;

/**
 * Renders JSON with syntax highlighting and collapsible objects
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const JSONRenderer: React.FC<{ data: any; depth?: number }> = ({ data, depth = 0 }) => {
  const [collapsed, setCollapsed] = useState<{ [key: string]: boolean }>({});
  
  const toggleCollapse = (key: string) => {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  };
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderValue = (value: any, key?: string, indent: number = 0): React.ReactElement => {
    const indentStr = '  '.repeat(indent);
    const keyPath = key ? `${depth}-${key}` : `${depth}-root`;
    
    if (value === null) {
      return <JSONNull>null</JSONNull>;
    }
    
    if (typeof value === 'string') {
      return <JSONString>&quot;{value}&quot;</JSONString>;
    }
    
    if (typeof value === 'number') {
      return <JSONNumber>{value}</JSONNumber>;
    }
    
    if (typeof value === 'boolean') {
      return <JSONBoolean>{value.toString()}</JSONBoolean>;
    }
    
    if (Array.isArray(value)) {
      if (value?.length === 0) {
        return <JSONBracket>[]</JSONBracket>;
      }
      
      return (
        <>
          <ToggleButton onClick={() => toggleCollapse(keyPath)}>
            {collapsed[keyPath] ? '▸' : '▾'}
          </ToggleButton>
          <JSONBracket>[</JSONBracket>
          {!collapsed[keyPath] && (
            <CollapsibleObject>
              {value.map((item, index) => (
                <div key={index}>
                  {indentStr}  {renderValue(item, `${keyPath}-${index}`, indent + 1)}
                  {index < value?.length - 1 && ','}
                </div>
              ))}
            </CollapsibleObject>
          )}
          {collapsed[keyPath] && <span>...</span>}
          <div>{indentStr}<JSONBracket>]</JSONBracket></div>
        </>
      );
    }
    
    if (typeof value === 'object') {
      const entries = Object.entries(value);
      if (entries?.length === 0) {
        return <JSONBracket>{'{}'}</JSONBracket>;
      }
      
      return (
        <>
          <ToggleButton onClick={() => toggleCollapse(keyPath)}>
            {collapsed[keyPath] ? '▸' : '▾'}
          </ToggleButton>
          <JSONBracket>{'{'}</JSONBracket>
          {!collapsed[keyPath] && (
            <CollapsibleObject>
              {entries.map(([k, v], index) => (
                <div key={k}>
                  {indentStr}  <JSONKey>&quot;{k}&quot;</JSONKey>: {renderValue(v, `${keyPath}-${k}`, indent + 1)}
                  {index < entries?.length - 1 && ','}
                </div>
              ))}
            </CollapsibleObject>
          )}
          {collapsed[keyPath] && <span>...</span>}
          <div>{indentStr}<JSONBracket>{'}'}</JSONBracket></div>
        </>
      );
    }
    
    return <span>{String(value)}</span>;
  };
  
  return <div>{renderValue(data)}</div>;
};

/**
 * QuickViewLogs Component
 * 
 * Displays a list of log records in an expandable format with JSON viewer
 * 
 * @component
 * @example
 * ```tsx
 * <QuickViewLogs 
 *   data={records}
 *   title="Example Records"
 *   subtitle="Sample log entries showing different log levels and data structures"
 *   getRecordLabel={(record, index) => `Example Log Entry ${index + 1} (${record.severity})`}
 * />
 * ```
 */
export const QuickViewLogs: React.FC<QuickViewLogsProps> = ({
  data,
  title = 'Records',
  subtitle = 'Sample log entries showing different log levels and data structures',
  className
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getLevel = (record: any) => {
    return record?.level || record?.message?.severity || record?.severity || 'INFO';
  };
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getTimestamp = (record: any) => {
    return record?.timestamp || record?.activity?.name || '';
  };
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getMessage = (record: any) => {
    if (record?.message && typeof record.message === 'string') {
      return record.message;
    }
    if (record?.message?.text) {
      return record.message.text;
    }
    if (record?.activity?.name) {
      return record.activity.name;
    }
    return 'No message available';
  };
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getSource = (record: any) => {
    return record?.source || record?.message?.category || '';
  };
  
  if (!data || data?.length === 0) {
    return null;
  }
  
  return (
    <Container className={className}>
      <Header>
        <Title>{title}</Title>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </Header>
      
      <RecordsList>
        {data.map((record, index) => {
          const accordionTitle = (
            <AccordionTitle>
              <LogLevelBadge $level={getLevel(record)}>
                {getLevel(record)}
              </LogLevelBadge>
              <RecordContent>
                <RecordTimestamp>{getTimestamp(record)}</RecordTimestamp>
                <RecordMessage>{getMessage(record)}</RecordMessage>
                {getSource(record) && <RecordSource>{getSource(record)}</RecordSource>}
              </RecordContent>
            </AccordionTitle>
          );
          
          return (
            <Accordion
              key={index}
              title={accordionTitle}
              defaultCollapsed={true}
            >
              <JSONViewerContainer>
                <JSONContainer>
                  <JSONRenderer data={record} depth={index} />
                </JSONContainer>
              </JSONViewerContainer>
            </Accordion>
          );
        })}
      </RecordsList>
    </Container>
  );
};

export default QuickViewLogs;

